/**
 * Layout Configuration
 *
 * Centralized layout settings for all routes.
 * Controls navigation visibility, hero sections, and styling.
 */

interface LayoutConfig {
  /** Hide global navigation entirely */
  hideNav?: boolean;
  /** Has full-bleed hero section (removes top padding) */
  hasHero?: boolean;
  /** Hero uses dark background (affects nav color) */
  darkHero?: boolean;
}

/**
 * Route-to-layout mappings
 * Key: pathname or pattern (for simple matching)
 */
const LAYOUT_MAP: Record<string, LayoutConfig> = {
  // Public routes
  '/login': {},

  // Legal routes
  '/terms': {},
  '/privacy': {},

  // Arena routes
  '/': { hasHero: true, darkHero: true },
  '/arena-home': { hasHero: true, darkHero: true },
  '/arena/:id': { hideNav: true, hasHero: true, darkHero: true },
  '/arena/:id/results': { hideNav: true, hasHero: true, darkHero: true },

  // Leaderboard routes
  '/leaderboard': { hasHero: true, darkHero: true },
  '/leaderboard/stats': { hasHero: true, darkHero: true },

  // Community routes
  '/community-votes': { hasHero: true, darkHero: true },

  // Eval routes
  '/eval': {},
  '/eval/prompts': {},
  '/eval/configs': {},
  '/eval/results': {},
  '/eval/llm-judge': {},

  // Admin routes
  '/configs': {},
  '/judge-configs': {},
  '/judge-configs/create': {},

  // 404 Not Found
  '*': { hasHero: true, darkHero: true },
};

/**
 * Match a pathname to a layout pattern
 */
function matchPattern(pathname: string, pattern: string): boolean {
  if (pattern === pathname) return true;
  if (pattern === '*') return true;

  // Convert route pattern to regex
  // /arena/:id -> /^\/arena\/[^/]+$/
  const regexPattern = pattern
    .replace(/:[^/]+/g, '[^/]+') // Replace :param with [^/]+
    .replace(/\//g, '\\/'); // Escape slashes

  const regex = new RegExp(`^${regexPattern}$`);
  return regex.test(pathname);
}

/**
 * Get layout configuration for current pathname
 */
export function getLayoutConfig(pathname: string) {
  // Find matching layout config
  let layoutConfig: LayoutConfig = {};

  for (const [pattern, config] of Object.entries(LAYOUT_MAP)) {
    if (matchPattern(pathname, pattern)) {
      layoutConfig = config;
      break;
    }
  }

  // Derive additional flags
  const isEvalRoute = pathname.startsWith('/eval');
  const isLoginRoute = pathname === '/login';

  return {
    hideNav: layoutConfig.hideNav ?? false,
    hasHero: layoutConfig.hasHero ?? false,
    darkHero: layoutConfig.darkHero ?? false,
    isEvalRoute,
    isLoginRoute,
  };
}
